import {compareAsc, parseISO} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';

import {isEmpty, isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {
  CommentsTab,
  ActivitySidebarAction,
  ActivitySidebar,
  ActivitySidebarV2,
  CustomerChat,
  CustomerChatIndicator,
  useChatSubscribe,
  useGetCurrentChatUser,
} from '@dms/features/activity-panel';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {ActivityTab} from './components/ActivityTab';

interface BusinessCaseActivityPanelProps {
  businessCaseId: string;
}

export function BusinessCaseActivityPanel(props: BusinessCaseActivityPanelProps) {
  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: props.businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const [canReadOnlineChat] = usePermissions({
    permissionKeys: ['readOnlineChat'],
    scopes: {
      readOnlineChat: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const chatConversations = businessCaseData?.chatConversations || [];

  const sortedChatConversations = chatConversations.toSorted((a, b) =>
    compareAsc(parseISO(a.createdAt), parseISO(b.createdAt))
  );
  const sortedChatConversationsIds = sortedChatConversations.map(
    ({conversationId}) => conversationId
  );
  const activeConversation = chatConversations.find((item) => item.isActive);

  const currentUserInConversation = useGetCurrentChatUser({
    conversationId: activeConversation?.conversationId,
  });

  const {isTyping} = useChatSubscribe({
    conversationId: activeConversation?.conversationId,
    currentUserId: currentUserInConversation?.id,
  });

  const actions = buildArray<ActivitySidebarAction>()
    .add({
      id: 'activity',
      label: i18n.t('entity.activityPanel.activity.title'),
      icon: 'sidebar/activity',
      component: <ActivityTab businessCaseId={props.businessCaseId} />,
      'data-testid': testIds.businessCase.selling('activity-menu-button'),
    })
    .when(
      isFeatureEnabled(featureFlags.CHAT_FEATURE) &&
        canReadOnlineChat &&
        businessCaseData?.businessCaseType !== 'BUYING',
      {
        id: 'chat',
        label: i18n.t('entity.activityPanel.chat.title'),
        icon: 'communication/customer_chat',
        indicator: (
          <CustomerChatIndicator
            conversationId={activeConversation?.conversationId || ''}
            isTyping={isTyping}
          />
        ),
        component: (
          // TODO: rethink if activeConversationId shouldn't be nullable, types would be better
          <CustomerChat
            activeConversationId={activeConversation?.conversationId ?? ''}
            conversationIds={sortedChatConversationsIds}
            businessCaseId={props.businessCaseId}
            customerId={businessCaseData?.customerId}
            isTyping={isTyping}
            isChattingDisabled={
              isNil(businessCaseData?.offerId) ||
              isEmpty(chatConversations) ||
              isNil(businessCaseData?.onlineOfferFirstSentAt)
            }
          />
        ),
        disablePadding: true,
      }
    )
    .add({
      id: 'comments',
      label: i18n.t('entity.activityPanel.comments.title'),
      icon: 'communication/comment',
      component: <CommentsTab businessCaseId={props.businessCaseId} />,
    });

  if (isFeatureEnabled(featureFlags.REDESIGN_ACTIVITY_SIDEBAR)) {
    return <ActivitySidebarV2 actions={actions} />;
  }

  return <ActivitySidebar actions={actions} />;
}
